import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoOptions, maskitoTransform } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { NgClass, NgIf, NgStyle } from '@angular/common';

@Component({
  selector: 'app-sin-input',
  templateUrl: './sin-input.component.html',
  styleUrls: ['./sin-input.component.scss'],
  standalone: true,
  imports: [MaskitoDirective, NgClass, NgIf, NgStyle],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SINInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class SINInputComponent implements ControlValueAccessor {
  @Input() controlId: string | null = null;
  @Input() showHideSINButton: boolean = true;
  @Input() error: boolean = false;
  @Input() placeholder: string = '';

  @Output() valueChange = new EventEmitter<string | null>();

  public showingSIN: boolean = false;

  public showMaskedSIN = false;

  public hiddenSINHtml = '';

  public showSINLabel = $localize`Show social insurance number`;
  public hideSINLabel = $localize`Hide social insurance number`;

  public maskOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]
  };

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public writeValue(value: string | null): void {
    this.value = value === null ? '' : this.getMaskedValue(value);
    this.showingSIN = false;
    this.refreshMaskedDisplay();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.showingSIN = false;
    this.refreshMaskedDisplay();
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value = inputValue;
    const unmaskedValue = this.getUnmaskedValue(inputValue);
    this.valueChange.emit(unmaskedValue);
    this.onChange(unmaskedValue);
  }

  public handleBlur(): void {
    this.onTouched();
    const unmaskedValue = this.getUnmaskedValue(this.value);
    if (unmaskedValue) {
      this.value = this.getMaskedValue(unmaskedValue);
    } else {
      this.value = '';
    }
    this.showingSIN = false;
    this.refreshMaskedDisplay();
  }

  public keepInputFocus(event: MouseEvent): void {
    event.preventDefault();
  }

  public toggleShowHide(): void {
    if (this.disabled) return;
    this.showingSIN = !this.showingSIN;
    this.refreshMaskedDisplay();
  }

  protected refreshMaskedDisplay(): void {
    this.showMaskedSIN = !this.showingSIN && (this.disabled || !!this.value);
    this.hiddenSINHtml = this.getHiddenSINString();
  }

  private getMaskedValue(inputValue: string): string {
    return maskitoTransform(inputValue, this.maskOptions);
  }

  private getUnmaskedValue(value: string): string {
    return value.replace(/\D/g, '');
  }

  private getHiddenSINString(): string {
    const input = this.value.replace(/\s/g, '') ?? '';
    if (!input) return '';
    const formattedCharacters: string[] = [];
    for (let i = 0; i < input.length; i++) {
      if (i > 0 && i % 3 === 0) {
        formattedCharacters.push(' ');
      }
      if (input.length >= 7 && i >= 6) {
        formattedCharacters.push(input[i]);
      } else {
        formattedCharacters.push(this.showingSIN ? input[i] : `<span class="masked-character">\u23FA</span>`);
      }
    }
    return formattedCharacters.join('');
  }
}
